'use client'

import { useState, type ReactNode } from 'react'
import { cx } from '@/app/lib/utils'
import { FieldMessage, useFieldIds } from '@/app/components/atoms/forms/field'
import styles from './TextField.module.scss'

export interface TextFieldProps {
  label: string
  value?: string
  placeholder?: string
  helperText?: string
  errorText?: string
  disabled?: boolean
  required?: boolean
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
  onChange?: (value: string) => void
  onBlur?: () => void
  onFocus?: () => void
  id?: string
  name?: string
  type?: 'text' | 'email' | 'tel' | 'url' | 'number'
  maxLength?: number
  autoComplete?: string
}

/**
 * Campo de texto del DS. Altura 56px, label flotante. El borde lo gestiona el
 * contenedor (no el input). La a11y de label/error se comparte con el resto de
 * campos vía `useFieldIds` + `FieldMessage`.
 */
export function TextField({
  label,
  value = '',
  placeholder,
  helperText,
  errorText,
  disabled = false,
  required = false,
  leadingIcon,
  trailingIcon,
  onChange,
  onBlur,
  onFocus,
  id,
  name,
  type = 'text',
  maxLength,
  autoComplete,
}: TextFieldProps) {
  const { fieldId, messageId } = useFieldIds(id)
  const [isFocused, setIsFocused] = useState(false)

  const hasValue = value.length > 0
  const hasError = Boolean(errorText)
  const hasMessage = Boolean(errorText || helperText)

  return (
    <div className={styles.root}>
      <div
        className={cx(
          styles.wrapper,
          isFocused && styles['wrapper--focused'],
          hasError && styles['wrapper--error'],
          disabled && styles['wrapper--disabled'],
          Boolean(leadingIcon) && styles['wrapper--leading'],
          Boolean(trailingIcon) && styles['wrapper--trailing'],
        )}
      >
        {leadingIcon && (
          <span className={styles.leadingIcon} aria-hidden="true">
            {leadingIcon}
          </span>
        )}

        <label htmlFor={fieldId} className={cx(styles.label, (isFocused || hasValue) && styles['label--elevated'])}>
          {label}
          {required && <span aria-hidden="true"> *</span>}
        </label>

        <input
          id={fieldId}
          name={name}
          type={type}
          value={value}
          placeholder={isFocused ? placeholder : undefined}
          disabled={disabled}
          required={required}
          maxLength={maxLength}
          autoComplete={autoComplete}
          aria-invalid={hasError || undefined}
          aria-describedby={hasMessage ? messageId : undefined}
          className={styles.input}
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={() => {
            setIsFocused(true)
            onFocus?.()
          }}
          onBlur={() => {
            setIsFocused(false)
            onBlur?.()
          }}
        />

        {trailingIcon && (
          <span className={styles.trailingIcon} aria-hidden="true">
            {trailingIcon}
          </span>
        )}
      </div>

      <FieldMessage id={messageId} error={errorText} helper={helperText} />
    </div>
  )
}
